import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { Button, Card } from '@parrot-software/design-system';
import { CircleDollarSign, CreditCard, Landmark, Loader2, RefreshCw } from '@parrot-software/design-system/icons';
import { toast } from 'sonner';
import type { SubscriptionBlockViewDto } from '@subscriptions/application/dtos';
import { formatCurrencyMXN } from '@subscriptions/application/utils';
import { PaymentMethodTypes } from '@subscriptions/domain/types/SubscriptionsConst';
import {
  CardPaymentMethodDetail,
  ReactivationClabeNotice,
  SpeiPaymentMethodDetail,
} from '@subscriptions/presentation/components/base';
import {
  REACTIVATION_ORIGIN,
  UPDATE_PAYMENT_ORIGIN,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/wizardOrigin';
import { useForcePayment } from '@subscriptions/presentation/hooks';
import { useAreSubscriptionActionsLocked } from '@subscriptions/presentation/hooks/useAreSubscriptionActionsLocked';

/** Maps the `<strong>` tag in i18n strings to a bold span (e.g. the SPEI exact-amount). */
const strongComponents = { strong: <strong className="font-semibold" /> };

interface BlockPaymentMethodCardProps {
  blockUuid: string;
  isLicenseAvailable?: boolean;
  paymentMethod: SubscriptionBlockViewDto['paymentMethod'];
  totalAmount: number;
}

/**
 * BlockPaymentMethodCard - Right card (~35% width) with payment method details
 *
 * Renders different layouts depending on payment method provider:
 * - SPEI (fintoc): CLABE display, bank info, warning text, "Ya realicé el pago" + change buttons.
 * - CARD (Stripe): brand + last-four ("Visa **** 1234"), expiry ("Vencimiento 12/27"),
 *   "Reintentar pago" + change buttons.
 * - No payment method: full-width button that re-enters the wizard at the invoice step.
 *
 * Action buttons (G2):
 * - "Cambiar método de pago" (both methods) → re-enters the wizard at the invoice step (C2).
 * - "Reintentar pago" (CARD only, license status ≠ AVAILABLE) → forcePayment(blockUuid) with a
 *   button-local spinner; success reloads the block detail, error surfaces a mapped toast.
 * - "Ya realicé el pago" (SPEI only, license status ≠ AVAILABLE) → only refreshes the block detail
 *   (no dedicated endpoint).
 *
 * @param {Object} props - Component properties
 * @param {string} props.blockUuid - Subscription block uuid, used to re-enter the wizard at the invoice step
 * @param {boolean} [props.isLicenseAvailable] - When true (license status AVAILABLE), hides the retry/already-paid buttons
 * @param {SubscriptionBlockViewDto['paymentMethod']} props.paymentMethod - Payment method data
 * @param {number} props.totalAmount - Block monthly total, shown as the exact SPEI transfer amount
 * @returns {React.ReactElement} Payment method card component
 *
 * @example
 * <BlockPaymentMethodCard blockUuid={block.uuid} paymentMethod={block.paymentMethod} totalAmount={block.total} />
 */
export const BlockPaymentMethodCard = ({
  blockUuid,
  isLicenseAvailable,
  paymentMethod,
  totalAmount,
}: BlockPaymentMethodCardProps) => {
  const { t } = useTranslation('subscriptions');
  const history = useHistory();
  const queryClient = useQueryClient();
  const { mutateAsync: forcePayment, isLoading: isRetrying } = useForcePayment();
  const areActionsLocked = useAreSubscriptionActionsLocked();

  // SPEI has no endpoint, so the reload itself is the mutation: react-query owns the
  // pending flag and always clears it.
  const { mutate: confirmSpeiPayment, isLoading: isConfirmingSpei } = useMutation(() =>
    queryClient.invalidateQueries(['subscriptions', 'blocks', 'detail', blockUuid]),
  );

  // SPEI warning shows the exact amount to transfer (block monthly total).
  const exactAmount = formatCurrencyMXN(totalAmount);

  const isSpei = paymentMethod?.methodType === PaymentMethodTypes.SPEI;
  const isCard = paymentMethod?.methodType === PaymentMethodTypes.CARD;

  // Only a churned block waiting on a reactivation transfer carries these: the amount above holds
  // for the day it was quoted, while the CLABE itself lasts until the cycle closes.
  const reactivation = paymentMethod?.reactivation;

  // Re-entry ("one step ahead") lands on the method-selection step; the wizard
  // then advances to fiscal data. Only the block creation is skipped. The
  // `origin` flag tells the wizard this is a payment-method change/configure
  // flow (not a block creation), so the confirmation step picks the right copy.
  // A block still in churn keeps the reactivation origin, or the wizard would
  // charge it as a regular cycle instead of quoting the reactivation.
  const handleChangePaymentMethod = () => {
    const origin = reactivation ? REACTIVATION_ORIGIN : UPDATE_PAYMENT_ORIGIN;
    history.push(`/subscriptions/blocks/create/${blockUuid}/payment?origin=${origin}`);
  };

  // CARD: retry the failed charge. Success reloads the detail via the hook's onSuccess;
  // error surfaces a generic toast — the specific backend code is rendered by the
  // lastError node (above the payment method), so the toast only signals the retry failed.
  const handleRetryPayment = async () => {
    try {
      await forcePayment(blockUuid);
    } catch {
      toast.error(t('blockDetail.paymentMethod.retryError'));
    }
  };

  return (
    <Card className="flex-1 p-6 shadow-xs">
      <div className="flex h-full flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <p className="text-sm text-muted-foreground">{t('blockDetail.paymentMethod.title')}</p>

          {paymentMethod && (
            <div className="flex items-center gap-1">
              {isSpei ? (
                <>
                  <Landmark className="w-5 h-5 text-muted-foreground" />
                  <p className="text-base font-semibold text-muted-foreground">{t('paymentMethod.spei')}</p>
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 text-muted-foreground" />
                  <p className="text-base font-semibold text-muted-foreground">{t('paymentMethod.creditOrDebit')}</p>
                </>
              )}
            </div>
          )}
        </div>

        {!paymentMethod && (
          <div className="flex flex-1 items-center">
            <Button className="w-full" disabled={areActionsLocked} onClick={handleChangePaymentMethod}>
              <CircleDollarSign className="mr-2 h-4 w-4" />
              {t('blockDetail.paymentMethod.configurePaymentMethod')}
            </Button>
          </div>
        )}

        {isCard && (
          <div className="flex flex-1 flex-col justify-center">
            <CardPaymentMethodDetail paymentMethod={paymentMethod} />
          </div>
        )}

        {isSpei && paymentMethod?.providerPublicId && (
          <div className="flex flex-1 flex-col justify-center">
            <SpeiPaymentMethodDetail className="w-full md:w-full" providerPublicId={paymentMethod.providerPublicId} />
          </div>
        )}

        {isSpei && paymentMethod?.providerPublicId && (
          <p className="text-sm text-foreground">
            <Trans
              components={strongComponents}
              i18nKey="subscriptions:blockDetail.paymentMethod.speiWarning"
              values={{ amount: exactAmount }}
            />
          </p>
        )}

        {isCard && (
          <p className="text-sm leading-5 text-foreground">{t('blockDetail.paymentMethod.cardAutoChargeNote')}</p>
        )}

        {isSpei && reactivation && (
          <ReactivationClabeNotice clabeValidUntil={reactivation.clabeValidUntil} quotedOn={reactivation.quotedOn} />
        )}

        {paymentMethod && (
          <div className="mt-auto flex flex-col gap-2 pt-2">
            {isCard && !isLicenseAvailable && (
              <Button className="w-full" disabled={isRetrying || areActionsLocked} onClick={handleRetryPayment}>
                {t('blockDetail.paymentMethod.retryPayment')}
                {isRetrying && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
              </Button>
            )}

            {isSpei && !isLicenseAvailable && (
              <Button
                className="w-full"
                disabled={isConfirmingSpei || areActionsLocked}
                onClick={() => confirmSpeiPayment()}
              >
                {t('blockDetail.paymentMethod.alreadyPaid')}
                {isConfirmingSpei && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
              </Button>
            )}

            <Button
              className="w-full"
              disabled={areActionsLocked}
              onClick={handleChangePaymentMethod}
              variant="outline"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('blockDetail.paymentMethod.changePaymentMethod')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
